"use client";

import { useApiClient } from "@/hooks/use-api-client";
import type { ListRulesApiResponse } from "@/lib/api-types";
import { RuleRule_status } from "@jace/api";
import type { components } from "@jace/api/src/generated/schema";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

type Rule = components["schemas"]["Rule"];

export const RULES_QUERY_KEY = ["rules"] as const;

interface RuleData {
	content: string;
	unified_uris: string[];
	rule_status?: RuleRule_status;
}

export function useRules() {
	const { client } = useApiClient();

	return useQuery<Rule[]>({
		queryKey: RULES_QUERY_KEY,
		queryFn: async () => {
			const response = await client.get<Rule[]>("/rules");
			if (response.error) {
				throw response.error;
			}
			return response.value;
		},
	});
}

export function useAddRule() {
	const { client } = useApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: RuleData) => {
			const response = await client.post<Rule>("/rules", { data });
			if (response.error) {
				throw response.error;
			}
			return response.value;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: RULES_QUERY_KEY });
		},
	});
}

export function useUpdateRule() {
	const { client } = useApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			ruleData,
			id,
		}: { ruleData: RuleData; id: string }) => {
			const validateFields = {
				content: ruleData.content.trim(),
				unified_uris:
					ruleData.unified_uris.length > 0 ? ruleData.unified_uris : [],
				rule_status: ruleData.rule_status,
			};

			const response = await client.put<Rule>(`/rules/${id}`, {
				data: validateFields,
			});
			if (response.error) {
				throw { content: [response.error.message] };
			}
			return response.value;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: RULES_QUERY_KEY });
		},
	});
}

export function useDeleteRule() {
	const { client } = useApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string) => {
			const response = await client.delete(`/rules/${id}`);
			if (response.error) {
				throw response.error;
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: RULES_QUERY_KEY });
		},
	});
}
